import Immutable from "immutable";
import * as types from "./constants";

const initialState = Immutable.fromJS({
  filesFetching: false,
  filesDidInvalidate: false,
  filesToAnnotate: {},
  possibleForms: {},
  matchedForms: {},
  castsInFiles: {},
  filesBeingAnnotated: [],
  filesBeingSaved: [],
  successfulAnnotations: [],
  annotationModalOpen: false,
  annotationModalCast: {},
  annotationModalForms: [],
  chartModalOpen: false,
  chartModalCast: {},
  mapModalOpen: false,
  mapModalCast: {},
  mapModalForm: {},
  chartDataFetching: false,
  chartData: [],
  mapSiteDataFetching: false,
  mapSiteData: [],
  annotationSnackbarOpen: false
});

function annotateReducer(state = initialState, action) {
  switch (action.type) {
    case types.SIGN_OUT_COMPLETE:
      return initialState;
    case types.WORKAREA_SET:
      return initialState;
    case types.ORGANIZATION_SET:
      return initialState;

    case types.ANNOTATE_FILES_INVALIDATE:
      return state.merge({
        filesDidInvalidate: true,
        filesBeingAnnotated: [],
        castsInFiles: {},
        possibleForms: {},
        matchedForms: {}
      });

    case types.ANNOTATE_FILES_FETCH_REQUEST:
      return state.set("filesFetching", true);

    case types.ANNOTATE_FILES_FETCH_SUCCESS:
      return state.merge({
        filesFetching: false,
        filesToAnnotate: action.payload.reduce(
          (R, x) => ({ ...R, [x.ctdFilePk]: x }),
          {}
        )
      });

    case types.ANNOTATE_FILES_FETCH_ERROR:
      return state.merge({ filesFetching: false, filesDidInvalidate: true });

    case types.ANNOTATE_CASTS_FETCH_REQUEST:
      return state;

    case types.ANNOTATE_CASTS_FETCH_SUCCESS:
      return state.mergeDeep({
        possibleForms: action.payload.reduce(
          (R, x) => ({ ...R, [x.ctdCastPk]: x.possibleForms }),
          {}
        ),
        matchedForms: action.payload.reduce(
          (R, x) => ({ ...R, [x.ctdCastPk]: x.matchedForm }),
          {}
        ),
        castsInFiles: Immutable.fromJS(
          action.payload.reduce((R, x) => ({ ...R, [x.ctdCastPk]: x }), {})
        )
          .map(x => x.delete("possibleForms").delete("matchedForm"))
          .groupBy(x => x.get("ctdFilePk").toString())
      });

    case types.ANNOTATE_CASTS_FETCH_ERROR:
      if (action.meta) {
        return state.update("filesBeingAnnotated", x =>
          x.toSet().delete(action.meta.ctdFilePk)
        );
      }
      return state;

    case types.ANNOTATE_CASTS_SET_MATCHED_FORM:
      return state.setIn(
        ["matchedForms", action.payload.ctdCastPk.toString()],
        Immutable.fromJS(action.payload.matchedForm)
      );

    case types.ANNOTATE_CASTS_UPDATE_MATCHED_FORM:
      return state.mergeIn(
        ["matchedForms", action.payload.ctdCastPk.toString()],
        Immutable.fromJS(action.payload.update)
      );

    case types.ANNOTATE_CASTS_UPDATE_CAST:
      return state.mergeIn(
        [
          "castsInFiles",
          action.payload.ctdFilePk.toString(),
          action.payload.ctdCastPk.toString()
        ],
        Immutable.fromJS(action.payload.update)
      );

    case types.ANNOTATE_FILES_REQUEST:
      return state.update("filesBeingSaved", x =>
        x.toSet().add(action.payload.ctdFilePk)
      );

    case types.ANNOTATE_FILES_SUCCESS:
      return state.withMutations(s =>
        s
          .update("filesBeingSaved", x =>
            x.toSet().delete(action.payload.ctdFilePk)
          )
          .update("filesBeingAnnotated", x =>
            x.toSet().delete(action.payload.ctdFilePk)
          )
          .update("castsInFiles", x =>
            x.delete(action.payload.ctdFilePk.toString())
          )
          .update("filesToAnnotate", x =>
            x.delete(action.payload.ctdFilePk.toString())
          )
          .update("successfulAnnotations", x =>
            x.toSet().add(action.payload.filename)
          )
      );

    case types.ANNOTATE_FILES_ERROR:
      return state.update("filesBeingSaved", x =>
        x.toSet().delete(action.meta.ctdFilePk)
      );

    case types.ANNOTATE_MODAL_OPEN:
      return state.set("annotationModalOpen", true);

    case types.ANNOTATE_MODAL_CLOSE:
      return state.set("annotationModalOpen", false);

    case types.ANNOTATE_MODAL_SET_CAST:
      return state.merge({ annotationModalCast: action.payload });

    case types.ANNOTATE_MODAL_SET_FORMS:
      return state.merge({ annotationModalForms: action.payload });

    case types.ANNOTATE_MODAL_SET_SELECTED:
      return state.merge({ annotationModalSelected: action.payload });

    case types.CHART_MODAL_OPEN:
      return state.set("chartModalOpen", true);

    case types.CHART_MODAL_CLOSE:
      return state.merge({
        chartModalOpen: false,
        chartModalCast: {},
        chartData: []
      });

    case types.MAP_MODAL_OPEN:
      return state.set("mapModalOpen", true);

    case types.MAP_MODAL_CLOSE:
      return state.merge({
        mapModalOpen: false
      });

    case types.CHART_MODAL_SET_CAST:
      return state.merge({ chartModalCast: action.payload });

    case types.CHART_MODAL_UPDATE_CAST:
      return state.mergeIn(
        ["chartModalCast"],
        Immutable.fromJS(action.payload)
      );

    case types.MAP_MODAL_SET_CAST: {
      return state.merge({ mapModalCast: action.payload });
    }

    case types.MAP_MODAL_SET_FORM: {
      return state.merge({ mapModalForm: action.payload });
    }

    case types.CHART_DATA_FETCH_REQUEST:
      return state.set("chartDataFetching", true);

    case types.CHART_DATA_FETCH_SUCCESS:
      return state.mergeDeep({
        chartDataFetching: false,
        chartData: action.payload
      });

    case types.CHART_DATA_FETCH_ERROR:
      return state.merge({
        chartDataFetching: false,
        chartModalCast: {}
      });

    case types.MAP_SITE_DATA_FETCH_REQUEST:
      return state.set("mapSiteDataFetching", true);

    case types.MAP_SITE_DATA_FETCH_SUCCESS:
      return state.mergeDeep({
        mapSiteDataFetching: false,
        mapSiteData: action.payload
      });

    case types.MAP_SITE_DATA_FETCH_ERROR:
      return state.merge({
        mapSiteDataFetching: false
      });

    case types.ANNOTATE_SNACKBAR_OPEN:
      return state.set("annotationSnackbarOpen", true);

    case types.ANNOTATE_SNACKBAR_CLOSE:
      return state.merge({
        annotationSnackbarOpen: false,
        successfulAnnotations: []
      });

    case types.ANNOTATE_ADD_FILE_TO_ANNOTATE:
      return state.update("filesBeingAnnotated", x =>
        x.toSet().add(action.payload.ctdFilePk)
      );

    case types.ANNOTATE_REMOVE_FILE_TO_ANNOTATE:
      return state.update("filesBeingAnnotated", x =>
        x.toSet().delete(action.payload.ctdFilePk)
      );

    default:
      return state;
  }
}

export default annotateReducer;
